import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { User, Mail, Lock, Building2, Phone, Loader2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formSchema = z.object({
  nome: z.string().min(2, 'Nome richiesto'),
  cognome: z.string().min(2, 'Cognome richiesto'),
  email: z.string().email('Email non valida'),
  password: z.string().min(6, 'Password deve essere almeno 6 caratteri'),
  azienda: z.string().optional(),
  telefono: z.string().optional(),
  privacy: z.boolean().refine(val => val === true, 'Devi accettare la privacy policy')
});

const RegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    resolver: zodResolver(formSchema)
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      login(response.data.access_token, response.data.user);
      toast.success('Registrazione completata!');
      navigate('/area-clienti');
    } catch (error) {
      console.error('Register error:', error);
      const message = error.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">SA</span>
            </div>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Crea il tuo account
          </h1>
          <p className="text-slate-600">
            Accedi all'area clienti e alle utility aziendali
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="nome"
                    {...register('nome')}
                    placeholder="Mario"
                    className="pl-10"
                    data-testid="register-nome"
                  />
                </div>
                {errors.nome && (
                  <p className="text-red-500 text-xs mt-1">{errors.nome.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  {...register('cognome')}
                  placeholder="Rossi"
                  className="mt-2"
                  data-testid="register-cognome"
                />
                {errors.cognome && (
                  <p className="text-red-500 text-xs mt-1">{errors.cognome.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="email@esempio.com"
                  className="pl-10"
                  data-testid="register-email"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="register-password"
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="azienda">Azienda (opzionale)</Label>
              <div className="relative mt-2">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="azienda"
                  {...register('azienda')}
                  placeholder="Nome azienda"
                  className="pl-10"
                  data-testid="register-azienda"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="telefono">Telefono (opzionale)</Label>
              <div className="relative mt-2">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="telefono"
                  {...register('telefono')}
                  placeholder="+39 333 1234567"
                  className="pl-10"
                  data-testid="register-telefono"
                />
              </div>
            </div>

            {/* Privacy Consent */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="privacy"
                  checked={watch('privacy')}
                  onCheckedChange={(checked) => setValue('privacy', checked)}
                  data-testid="register-privacy-checkbox"
                />
                <div className="text-sm">
                  <Label htmlFor="privacy" className="font-normal text-slate-600 cursor-pointer">
                    Ho letto e accetto la{' '}
                    <Link to="/privacy-policy" className="text-indigo-600 hover:underline" target="_blank">
                      Privacy Policy
                    </Link>{' '}
                    e la{' '}
                    <Link to="/cookie-policy" className="text-indigo-600 hover:underline" target="_blank">
                      Cookie Policy
                    </Link>. *
                  </Label>
                  {errors.privacy && (
                    <p className="text-red-500 text-xs mt-1">{errors.privacy.message}</p>
                  )}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full btn-accent"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Registrazione in corso...
                </>
              ) : (
                <>
                  Registrati
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">
              Hai già un account?{' '}
              <Link to="/login" className="text-indigo-600 hover:underline font-medium">
                Accedi
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
